import React, { useState, useEffect, useCallback } from 'react';
import { Bug, CheckCircle, RotateCcw, RefreshCw, X } from 'lucide-react';
import apiService from '../apiService';
import { useLiveRefresh } from '../liveRefresh';

/**
 * Erreurs — supervision maison (étape V2 sécurité, 2026-10-24).
 * Erreurs du serveur et erreurs JavaScript des apps, regroupées par type, conservées 30 jours.
 * Alertes e-mail à info@mbiyo.app et support@taxicenterapp.com (nouvelle erreur, répétition
 * au plus 1×/heure, récapitulatif quotidien). Accès : super-administrateur et rôle IT.
 * ⚠ apiService.get admin IGNORE l'objet params → query string dans l'URL.
 */

const SOURCES = [
  { key: '', label: 'Toutes' },
  { key: 'server', label: 'Serveur' },
  { key: 'client', label: 'App Client' },
  { key: 'driver', label: 'App Chauffeur' },
  { key: 'supplier', label: 'App Business' },
  { key: 'admin', label: 'Admin web' },
  { key: 'web', label: 'Site web' },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.filter(s => s.key).map(s => [s.key, s.label]));
const SOURCE_COLOR = { server: '#1E2229', client: '#F54119', driver: '#642C90', supplier: '#047857', admin: '#2563EB', web: '#0E7490' };

const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const card = { background: 'white', borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '0.75rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' };
const chip = (on) => ({ border: '1px solid ' + (on ? '#1E2229' : '#DCE0E6'), background: on ? '#1E2229' : '#fff', color: on ? '#fff' : '#1E2229', borderRadius: 999, padding: '5px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' });

function SourceBadge({ source }) {
  return (
    <span style={{ background: (SOURCE_COLOR[source] || '#5C636E') + '18', color: SOURCE_COLOR[source] || '#5C636E', borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {SOURCE_LABEL[source] || source}
    </span>
  );
}

function ErrorDetail({ id, onClose, onChanged }) {
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    apiService.get(`/admin/errors/${id}`, { userType: 'admin' }).then(r => setItem(r.item || null)).catch(() => setItem(null));
  }, [id]);
  const act = async (action) => {
    setBusy(true);
    try {
      await apiService.patch(`/admin/errors/${id}`, { action }, { userType: 'admin' });
      onChanged();
      onClose();
    } catch (e) { alert(e?.error || 'Action impossible'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 'min(860px, 96vw)', maxHeight: '90vh', overflowY: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h3 style={{ margin: 0 }}>Détail de l'erreur</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        {!item ? <p style={{ color: '#9AA0A8' }}>Chargement…</p> : (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '10px 0' }}>
              <SourceBadge source={item.source} />
              <span style={{ fontSize: '0.78rem', color: '#5C636E' }}>{item.kind} · {item.name}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 700, color: item.status === 'new' ? '#B91C1C' : '#047857' }}>{item.status === 'new' ? 'Non résolue' : 'Résolue'}</span>
            </div>
            <p style={{ fontWeight: 700, fontSize: '1rem', margin: '6px 0' }}>{item.message}</p>
            <table style={{ width: '100%', fontSize: '0.84rem', borderCollapse: 'collapse', margin: '8px 0' }}>
              <tbody>
                {[
                  ['Où', item.route],
                  ['Code HTTP', item.http_status],
                  ['Occurrences', item.count],
                  ['Première fois', fmt(item.first_seen)],
                  ['Dernière fois', fmt(item.last_seen)],
                  ['Version de l\'app', item.app_version],
                  ['Mise à jour (OTA)', item.update_id],
                  ['Système', [item.platform, item.os_version].filter(Boolean).join(' ')],
                  ['Appareil', item.device_model],
                  ['Dernier compte concerné', item.last_user_role ? `${item.last_user_role} ${item.last_user_id || ''}` : null],
                  ['Réapparitions après résolution', item.reopened_count || null],
                  ['Résolue par', item.resolved_by ? `${item.resolved_by} — ${fmt(item.resolved_at)}` : null],
                ].filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
                  <tr key={k}><td style={{ padding: '4px 8px', color: '#5C636E', width: 210 }}>{k}</td><td style={{ padding: '4px 8px' }}>{String(v)}</td></tr>
                ))}
              </tbody>
            </table>
            {item.stack && (
              <pre style={{ background: '#0F172A', color: '#E2E8F0', borderRadius: 10, padding: 12, fontSize: '0.74rem', overflowX: 'auto', whiteSpace: 'pre-wrap', maxHeight: 320 }}>{item.stack}</pre>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
              {item.status === 'new'
                ? <button disabled={busy} onClick={() => act('resolve')} style={{ display: 'flex', gap: 6, alignItems: 'center', background: '#047857', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontWeight: 700, cursor: 'pointer' }}><CheckCircle size={16} /> Marquer comme résolue</button>
                : <button disabled={busy} onClick={() => act('reopen')} style={{ display: 'flex', gap: 6, alignItems: 'center', background: '#F4F5F7', color: '#1E2229', border: 'none', borderRadius: 10, padding: '8px 14px', fontWeight: 700, cursor: 'pointer' }}><RotateCcw size={16} /> Rouvrir</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ErrorsPage() {
  const [status, setStatus] = useState('new');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ items: [], total: 0, open_by_source: {} });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ status, ...(source ? { source } : {}), ...(q.trim() ? { q: q.trim() } : {}) }).toString();
      const r = await apiService.get(`/admin/errors?${qs}`, { userType: 'admin' });
      if (r?.success) setData(r);
    } catch (_) {}
    finally { setLoading(false); }
  }, [status, source, q]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), ['error']);

  const openTotal = Object.values(data.open_by_source || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Bug size={28} /> Erreurs</h1>
        <div className="page-actions"><button className="btn-secondary" onClick={load}><RefreshCw size={16} /> Actualiser</button></div>
      </div>

      <div style={{ ...card, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div><div style={{ fontSize: '0.75rem', color: '#5C636E', fontWeight: 600 }}>NON RÉSOLUES</div><div style={{ fontSize: '1.6rem', fontWeight: 800, color: openTotal ? '#B91C1C' : '#047857' }}>{openTotal}</div></div>
        {SOURCES.filter(s => s.key).map(s => (
          <div key={s.key}><div style={{ fontSize: '0.72rem', color: '#5C636E' }}>{s.label}</div><div style={{ fontWeight: 700 }}>{data.open_by_source?.[s.key] || 0}</div></div>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#9AA0A8', maxWidth: 360 }}>
          Alertes e-mail : info@mbiyo.app et support@taxicenterapp.com. Conservation : 30 jours après la dernière occurrence.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '0.5rem 0 0.9rem' }}>
        {[['new', 'Non résolues'], ['resolved', 'Résolues'], ['all', 'Toutes']].map(([k, l]) => (
          <button key={k} style={chip(status === k)} onClick={() => setStatus(k)}>{l}</button>
        ))}
        <span style={{ width: 12 }} />
        {SOURCES.map(s => <button key={s.key || 'all'} style={chip(source === s.key)} onClick={() => setSource(s.key)}>{s.label}</button>)}
        <input
          placeholder="Rechercher (message, écran, route)…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ marginLeft: 'auto', minWidth: 240, padding: '7px 12px', border: '1px solid #DCE0E6', borderRadius: 999, fontSize: '0.85rem' }}
        />
      </div>

      {loading ? <div style={{ ...card, color: '#9AA0A8', textAlign: 'center' }}>Chargement…</div>
        : data.items.length === 0 ? <div style={{ ...card, color: '#047857', textAlign: 'center', padding: '2.5rem' }}>Aucune erreur pour ces filtres.</div>
        : (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr style={{ background: '#F4F5F7', textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px' }}>Origine</th><th style={{ padding: '10px 12px' }}>Message</th><th style={{ padding: '10px 12px' }}>Où</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Fois</th><th style={{ padding: '10px 12px' }}>Dernière fois</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(it => (
                  <tr key={it._id} onClick={() => setOpenId(it._id)} style={{ borderTop: '1px solid #ECEEF1', cursor: 'pointer' }}>
                    <td style={{ padding: '9px 12px' }}><SourceBadge source={it.source} /></td>
                    <td style={{ padding: '9px 12px', maxWidth: 460 }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.message}</div>
                      {it.app_version && <div style={{ fontSize: '0.74rem', color: '#9AA0A8' }}>v{it.app_version}{it.platform ? ` · ${it.platform}` : ''}{it.device_model ? ` · ${it.device_model}` : ''}</div>}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#5C636E', fontSize: '0.8rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.route || '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700 }}>{it.count}</td>
                    <td style={{ padding: '9px 12px', color: '#5C636E', whiteSpace: 'nowrap' }}>{fmt(it.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {openId && <ErrorDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
